import { useContext, useMemo } from "react";
import { useRegisterActions } from "@refinedev/kbar";
import { useKBar } from "kbar";
import type { Action } from "kbar";
import {
  useSetLocale,
  useGetLocale,
  useLogout,
  useNotification,
} from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useMarketplaceStore } from "../store/useMarketplaceStore";
import {
  useWorkspaceStore,
  OrderSnapshot,
  ProductSnapshot,
} from "../store/useWorkspaceStore";
import { useRecentOrdersStore } from "../store/useRecentOrdersStore";
import { ColorModeContext } from "../providers/colorProvider";
import { axiosInstance } from "../utils/axios";
import { API_URL } from "../providers/dataProvider";

const AMAZON_ORDER_ID_REGEX = /^\d{3}-\d{7}-\d{7}$/;
const ASIN_REGEX = /^[A-Z0-9]{10}$/i;

type SavedFilterTab = { id: string; name: string };
type NavTuple = readonly [
  id: string,
  name: string,
  path: string,
  keywords: string,
];

// Sayfa ve Hızlı Erişim haritalamalarını tekilleştiren yardımcı fonksiyon
const createNavActions = (
  items: readonly NavTuple[],
  section: string,
  navigate: (_to: string) => void,
): Action[] =>
  items.map(([id, name, path, keywords]) => ({
    id,
    name,
    section,
    keywords,
    perform: () => navigate(path),
  }));

const readSavedViews = (
  scope: string,
  basePath: string,
  navigate: (_to: string) => void,
): Action[] => {
  try {
    const raw = localStorage.getItem(`${scope}-saved-filter-tabs`);
    const tabs: SavedFilterTab[] = raw ? JSON.parse(raw) : [];
    return tabs.map((tab) => ({
      id: `saved-view-${scope}-${tab.id}`,
      name: tab.name,
      subtitle: scope === "orders" ? "Siparişler" : "Envanter",
      section: "Kayıtlı Görünümler",
      keywords: `${tab.name} kayıtlı görünüm saved view filtre ${scope}`,
      perform: () => navigate(`${basePath}?tab=saved_${tab.id}`),
    }));
  } catch {
    return [];
  }
};

export const useKbarActions = () => {
  const { toggleMode, mode } = useContext(ColorModeContext);
  const setLocale = useSetLocale();
  const getLocale = useGetLocale();
  const currentLocale = getLocale();
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();
  const { open: openNotification } = useNotification();

  const countries = useMarketplaceStore((s) => s.countries);
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const selectCountry = useMarketplaceStore((s) => s.selectCountry);

  const workspaceItems = useWorkspaceStore((s) => s.items);
  const openWorkspace = useWorkspaceStore((s) => s.open);
  const clearWorkspace = useWorkspaceStore((s) => s.clearAll);
  const addWorkspaceNote = useWorkspaceStore((s) => s.addNote);

  const recentOrders = useRecentOrdersStore((s) => s.orders);

  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }));
  const query = searchQuery.trim();

  // 1. Statik Eylemler
  const staticActions = useMemo<Action[]>(
    () => [
      {
        id: "toggle-dark-mode",
        name: mode === "dark" ? "Açık Tema" : "Koyu Tema",
        section: "Ayarlar",
        keywords: "dark light tema mod theme",
        perform: toggleMode,
      },
      {
        id: "lang-tr",
        name: "Türkçe",
        subtitle: currentLocale === "tr" ? "Aktif" : undefined,
        section: "Dil",
        keywords: "turkish dil language tr türkçe",
        perform: () => setLocale?.("tr"),
      },
      {
        id: "lang-en",
        name: "English",
        subtitle: currentLocale === "en" ? "Active" : undefined,
        section: "Dil",
        keywords: "english dil language en ingilizce",
        perform: () => setLocale?.("en"),
      },
      {
        id: "workspace-open",
        name: "Workspace'i Aç",
        section: "Workspace",
        keywords: "workspace pin open aç panel",
        perform: openWorkspace,
      },
      {
        id: "workspace-clear",
        name: "Workspace'i Temizle",
        section: "Workspace",
        keywords: "workspace clear temizle sil kaldır",
        perform: clearWorkspace,
      },
      {
        id: "logout",
        name: "Çıkış Yap",
        section: "Hesap",
        keywords: "logout çıkış sign out oturum kapat",
        perform: () => logout(),
      },
    ],
    [
      mode,
      toggleMode,
      currentLocale,
      setLocale,
      openWorkspace,
      clearWorkspace,
      logout,
    ],
  );

  // 2. Sayfa ve Hızlı Erişim Verileri (Daha temiz veri setleri)
  const allNavActions = useMemo<Action[]>(() => {
    const pages = [
      ["nav-dashboard", "Dashboard", "/", "anasayfa home dashboard"],
      ["nav-orders", "Siparişler", "/order", "orders sipariş listesi"],
      [
        "nav-inventory",
        "Envanter",
        "/inventory",
        "inventory ürünler stok products",
      ],
      [
        "nav-store-settings",
        "Mağaza Ayarları",
        "/store/store-settings",
        "store settings mağaza ayar",
      ],
      [
        "nav-pricing",
        "Fiyatlandırma Ayarları",
        "/store/pricing-settings",
        "pricing fiyat ayar",
      ],
      [
        "nav-mail",
        "Mail Bildirim Ayarları",
        "/store/mail-notification-settings",
        "mail bildirim notification",
      ],
      [
        "nav-competition",
        "Rekabet Ayarları",
        "/store/competition-settings",
        "competition rekabet buybox",
      ],
      [
        "nav-restrictions",
        "Kısıtlamalar",
        "/store-security/restrictions",
        "restrictions kısıtlama güvenlik marka brand",
      ],
      [
        "nav-filters",
        "Filtreler",
        "/store-security/filters",
        "filters filtre güvenlik",
      ],
      ["nav-extension", "Extension", "/extension", "extension eklenti"],
      ["nav-warehouse", "Depo Servisi", "/warehouse", "warehouse depo servis"],
      [
        "nav-store-create",
        "Mağaza Oluştur",
        "/store/create",
        "create store mağaza oluştur yeni",
      ],
    ] as const;

    const quickAccess = [
      [
        "deep-smart-pricing",
        "Smart Pricing",
        "/store/store-settings?tab=smart-pricing",
        "smart pricing fiyat mağaza ayar",
      ],
      [
        "deep-invoice-settings",
        "Fatura Ayarları",
        "/store/store-settings?tab=invoice",
        "invoice fatura ayar",
      ],
      [
        "deep-order-management",
        "Sipariş Yönetimi Ayarları",
        "/store/store-settings?tab=order-management",
        "order management sipariş yönetim ayar",
      ],
      [
        "deep-inventory-settings",
        "Envanter Ayarları",
        "/store/store-settings?tab=inventory",
        "inventory envanter ayar",
      ],
      [
        "deep-danger-zone",
        "Danger Zone",
        "/store/store-settings?tab=danger-zone",
        "danger zone tehlike mağaza sil",
      ],
      [
        "deep-restrict-brand",
        "Kısıtlı Markalar",
        "/store-security/restrictions?tab=brand",
        "restricted brand marka kısıtlı",
      ],
      [
        "deep-restrict-category",
        "Kısıtlı Kategoriler",
        "/store-security/restrictions?tab=category",
        "restricted category kategori kısıtlı",
      ],
      [
        "deep-restrict-keyword",
        "Kısıtlı Kelimeler",
        "/store-security/restrictions?tab=keyword",
        "restricted keyword kelime kısıtlı",
      ],
      [
        "deep-restrict-product",
        "Kısıtlı Ürünler",
        "/store-security/restrictions?tab=product",
        "restricted product ürün kısıtlı",
      ],
      [
        "deep-filter-source",
        "Kaynak Filtreleri",
        "/store-security/filters?tab=source",
        "source filter kaynak filtre",
      ],
      [
        "deep-filter-target",
        "Hedef Filtreleri",
        "/store-security/filters?tab=target",
        "target filter hedef filtre",
      ],
      [
        "deep-mail-forwarded",
        "Yönlendirilen Mailler",
        "/store/mail-notification-settings?tab=forwarded-mails",
        "forwarded mail yönlendirilen",
      ],
      [
        "deep-competition-exceptions",
        "Rekabet İstisna Listesi",
        "/store/competition-settings?tab=exception-list",
        "exception istisna rekabet",
      ],
    ] as const;

    return [
      ...createNavActions(pages, "Sayfalar", navigate),
      ...createNavActions(quickAccess, "Hızlı Erişim", navigate),
    ];
  }, [navigate]);

  // 3. Dinamik ve Mağaza Bazlı Eylemler
  const dynamicActions = useMemo<Action[]>(() => {
    const actions: Action[] = [
      ...readSavedViews("orders", "/order", navigate),
      ...readSavedViews("inventory", "/inventory", navigate),
      ...recentOrders.map((order) => ({
        id: `recent-order-${order.id}`,
        name: order.amazonOrderId,
        subtitle: order.itemName,
        section: "Son Görüntülenen Siparişler",
        keywords: `recent son görüntülenen ${order.amazonOrderId} ${order.itemName ?? ""}`,
        perform: () => navigate(`/order-detail/${order.id}`),
      })),
      ...countries.map((country) => ({
        id: `marketplace-${country.countryCode}`,
        name: country.countryName,
        subtitle: country.storeName ?? country.name,
        section: "Mağaza",
        keywords: `${country.name} ${country.countryCode} ${country.countryName} mağaza store marketplace`,
        perform: () => selectCountry(country.countryCode),
      })),
      ...workspaceItems
        .filter((i) => i.type !== "note")
        .map((item) => {
          if (item.type === "order") {
            const s = item.snapshot as OrderSnapshot;
            return {
              id: `pin-order-${item.uid}`,
              name: s.amazonOrderId,
              subtitle: s.itemName,
              section: "Pinli Siparişler",
              keywords: `order sipariş ${s.amazonOrderId} ${s.itemName ?? ""}`,
              perform: () => navigate(`/order-detail/${s.id}`),
            };
          }
          const s = item.snapshot as ProductSnapshot;
          return {
            id: `pin-product-${item.uid}`,
            name: s.asin,
            subtitle: s.itemName,
            section: "Pinli Ürünler",
            keywords: `product ürün ${s.asin} ${s.itemName ?? ""}`,
            perform: () => navigate(`/inventory/product/${s.asin}`),
          };
        }),
    ];

    return actions;
  }, [navigate, recentOrders, countries, selectCountry, workspaceItems]);

  // 4. Akıllı Arama Eylemleri
  const smartActions = useMemo<Action[]>(() => {
    const actions: Action[] = [];
    if (!query) return actions;

    if (AMAZON_ORDER_ID_REGEX.test(query)) {
      actions.push({
        id: "smart-goto-order",
        name: `Siparişe git: ${query}`,
        section: "Akıllı Arama",
        keywords: query,
        perform: async () => {
          if (!selectedCountry) return;
          try {
            const predicate = JSON.stringify({
              type: "and",
              predicates: [
                { property: "searchTerm", type: "eq", value: query },
              ],
            });
            const response = await axiosInstance.get(
              `${API_URL}/store/${selectedCountry.id}/order/optimized?page=0&size=2&predicate=${encodeURIComponent(predicate)}`,
            );
            const orders = response.data?.responseData ?? [];
            if (orders.length > 0) {
              navigate(`/order-detail/${orders[0].id}`);
            } else {
              openNotification?.({
                type: "error",
                message: "Sipariş bulunamadı",
                description: query,
              });
            }
          } catch {
            openNotification?.({
              type: "error",
              message: "Sipariş araması başarısız oldu",
            });
          }
        },
      });
    } else if (ASIN_REGEX.test(query)) {
      const asin = query.toUpperCase();
      actions.push({
        id: "smart-goto-product",
        name: `Ürüne git: ${asin}`,
        section: "Akıllı Arama",
        keywords: query,
        perform: () => navigate(`/inventory/product/${asin}`),
      });
    }

    if (query.toLowerCase() === "game") {
      actions.push({
        id: "easter-egg-game",
        name: "Gizli Oyun 🎮",
        section: "Akıllı Arama",
        keywords: "game",
        perform: () => navigate("/easter-game"),
      });
    }

    if (query.length >= 3) {
      actions.push({
        id: "smart-add-note",
        name: `Workspace'e not ekle: "${query}"`,
        section: "Akıllı Arama",
        keywords: query,
        perform: () => {
          addWorkspaceNote(query);
          openWorkspace();
        },
      });
    }

    return actions;
  }, [
    query,
    selectedCountry,
    navigate,
    openNotification,
    addWorkspaceNote,
    openWorkspace,
  ]);

  // Tüm eylemlerin tek bir çatı altında birleştirilmesi
  const combinedActions = useMemo<Action[]>(
    () => [
      ...smartActions,
      ...staticActions,
      ...allNavActions,
      ...dynamicActions,
    ],
    [smartActions, staticActions, allNavActions, dynamicActions],
  );

  useRegisterActions(combinedActions, [combinedActions]);
};
